import { CloseCircleOutlined, FilterOutlined } from '@ant-design/icons'
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid2 as Grid,
  IconButton,
  Menu,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import ButtonActionBar from 'components/ButtonActionBar/ButtonActionBar'
import MultiOrganizationSelector from 'components/MultiOrganizationSelector'
import SearchInput from 'components/SearchInput'
import SimpleTitle from 'components/SimpleTitle'
import useAuth from 'hooks/useAuth'
import { useArchiveComponents, useSearchComponents, useUnarchiveComponents } from 'hooks/useComponents'
import useLocalStorage from 'hooks/useLocalStorage'
import type { FC } from 'react'
import React, { useCallback, useEffect, useState } from 'react'
import type { Component, ComponentQueryParams, ComponentSearchResponse } from 'types/components'
import { getOrganizationLabelMap, getRootOrganizationId } from 'types/user-profile'
import ArchiveConfirmationDialog from '../ArchiveConfirmationDialog'
import ComponentsTable from '../ComponentsTable'

const ManageComponents: FC = () => {
  const { token, profile, isLoading: isLoadingProfile } = useAuth()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedOrganizations, setSelectedOrganizations] = useLocalStorage<string[]>(
    'components-selected-organizations',
    [],
    profile?.user?.id
  )
  const [page, setPage] = useLocalStorage<number>('components-page', 0, profile?.user?.id)
  const [rowsPerPage, setRowsPerPage] = useLocalStorage<number>('components-rows-per-page', 25, profile?.user?.id)
  const [sort, setSort] = useLocalStorage<string>('components-sort', 'label', profile?.user?.id)
  const [order, setOrder] = useLocalStorage<string>('components-order', 'asc', profile?.user?.id)

  const [showActive, setShowActive] = useLocalStorage<boolean>('components-show-active', true, profile?.user?.id)
  const [showArchived, setShowArchived] = useLocalStorage<boolean>('components-show-archived', false, profile?.user?.id)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)
  const [moreFiltersAnchor, setMoreFiltersAnchor] = useState<null | HTMLElement>(null)

  const [organizationLabelMap, setOrganizationLabelMap] = useState<{ [key: string]: string }>({})
  const [isLoading, setIsLoading] = useState(false)
  const [components, setComponents] = useState<ComponentSearchResponse>({
    components: [],
    paginationInfo: {
      currentPage: 0,
      totalPages: 0,
      totalItems: 0,
      perPage: 0,
    },
  })

  const theme = useTheme()

  // Determine archive filter based on show toggles
  const getArchivedFilter = (): boolean | undefined => {
    if (showActive && !showArchived) return false // Only active
    if (!showActive && showArchived) return true // Only archived
    return undefined // Both or neither - show all
  }

  const queryParams: ComponentQueryParams = {
    organizationIds: selectedOrganizations,
    periodIds: [],
    userIds: [],
    searchTerm,
    pagination: {
      page: page + 1,
      perPage: rowsPerPage,
    },
    sort: [{ field: sort, order }],
    isArchived: getArchivedFilter(),
  }

  const { componentSearchResult, isLoading: isLoadingComponents, revalidate } = useSearchComponents(token!, queryParams)

  const { archiveComponents } = useArchiveComponents(token!)
  const { unarchiveComponents } = useUnarchiveComponents(token!)

  useEffect(() => {
    setIsLoading(isLoadingComponents || isLoadingProfile)
  }, [isLoadingComponents, isLoadingProfile])

  useEffect(() => {
    if (componentSearchResult?.components) {
      console.log('Updating components state with new search results:', componentSearchResult.components.length)
      setComponents(componentSearchResult)
    }
  }, [componentSearchResult])

  useEffect(() => {
    if (profile) {
      const newOrganizationLabelMap = getOrganizationLabelMap(profile)
      setOrganizationLabelMap(newOrganizationLabelMap)
      console.log(organizationLabelMap)
    }
  }, [profile])

  useEffect(() => {}, [selectedOrganizations, searchTerm])

  // Handle sorting
  const handleSort = (property: string) => {
    const isAscending = sort === property && order === 'asc'
    setOrder(isAscending ? 'desc' : 'asc')
    setSort(property)
  }

  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value)
  }, [])

  const handleOrganizationChange = (newOrganizations: string[]) => {
    setSelectedOrganizations(newOrganizations)
    setPage(0)
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value)
    setPage(0)
  }

  const handleArchiveClick = (component: Component) => {
    if (!component.id) return
    setSelectedIds([component.id])
    setOpenConfirmDialog(true)
  }

  const selectedComponents = components.components.filter((c) => c.id != null && selectedIds.includes(c.id))

  // Determine if selected items are all archived, all active, or mixed
  const getSelectedArchiveState = (): 'archived' | 'active' | 'mixed' => {
    if (selectedComponents.length === 0) return 'mixed'
    const allArchived = selectedComponents.every((c) => c.isArchived)
    const allActive = selectedComponents.every((c) => !c.isArchived)
    if (allArchived) return 'archived'
    if (allActive) return 'active'
    return 'mixed'
  }

  const handleConfirmArchive = () => {
    const rootOrgId = profile ? getRootOrganizationId(profile) : null
    if (!rootOrgId) return

    const action = getSelectedArchiveState() === 'active' ? archiveComponents : unarchiveComponents
    action({ componentIds: selectedIds, rootOrganizationId: rootOrgId })
      .then(() => {
        setSelectedIds([])
        setOpenConfirmDialog(false)
        revalidate()
      })
      .catch(() => {
        // Error is handled by the hook
      })
  }

  return (
    <>
      <Grid container pb={2}>
        <Grid size={{ xs: 4 }}>
          <SimpleTitle title="Components" />
        </Grid>
        <Grid size={{ xs: 8 }} />
      </Grid>
      <Grid container spacing={2} mb={3} alignItems="center">
        <Grid size={{ xs: 3 }}>
          <SearchInput
            id="component-search-input"
            key="component-search-input"
            onSearch={handleSearch}
            initialValue={searchTerm}
            labelBackgroundColor={theme.palette.background.default}
          />
        </Grid>
        <Grid size={{ xs: 5 }}></Grid>
        <Grid size={{ xs: 2 }}>
          <MultiOrganizationSelector
            userProfile={profile!}
            selectedOrganizationIds={selectedOrganizations}
            handleChange={handleOrganizationChange}
            labelBackgroundColor={theme.palette.background.default}
          />
        </Grid>
        <Grid size={{ xs: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={(e) => setMoreFiltersAnchor(e.currentTarget)}
              startIcon={<FilterOutlined />}
              sx={{ minWidth: 140 }}
            >
              More Filters
            </Button>
            <Tooltip title="Clear Filters">
              <IconButton
                color="primary"
                onClick={() => {
                  setSelectedOrganizations([])
                  setSearchTerm('')
                  setShowActive(true)
                  setShowArchived(false)
                }}
              >
                <CloseCircleOutlined />
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>
      </Grid>
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
          <CircularProgress />
        </Box>
      ) : (
        <ComponentsTable
          components={components.components}
          page={page}
          rowsPerPage={rowsPerPage}
          sort={sort}
          order={order}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          handleSort={handleSort}
          totalItems={components.paginationInfo.totalItems}
          selectable
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          onArchiveClick={handleArchiveClick}
        />
      )}

      {/* More Filters Menu */}
      <Menu
        anchorEl={moreFiltersAnchor}
        open={Boolean(moreFiltersAnchor)}
        onClose={() => setMoreFiltersAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: { minWidth: '280px', mt: 1 },
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="overline" sx={{ display: 'block', color: 'text.secondary' }}>
            Components
          </Typography>
          <Stack spacing={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={showActive}
                  onChange={(e) => {
                    setShowActive(e.target.checked)
                    setPage(0)
                  }}
                  size="small"
                />
              }
              label="Show Active"
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.875rem',
                },
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showArchived}
                  onChange={(e) => {
                    setShowArchived(e.target.checked)
                    setPage(0)
                  }}
                  size="small"
                />
              }
              label="Show Archived"
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.875rem',
                },
              }}
            />
          </Stack>
        </Box>
      </Menu>

      <ButtonActionBar show={selectedIds.length > 0} maxWidth="900px" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={2}>
          <Typography sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
            {selectedIds.length} item{selectedIds.length > 1 ? 's' : ''} selected
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<CloseCircleOutlined />}
            onClick={() => setSelectedIds([])}
          >
            Cancel
          </Button>
          {getSelectedArchiveState() !== 'mixed' && (
            <Button
              variant="contained"
              color={getSelectedArchiveState() === 'active' ? 'warning' : 'primary'}
              onClick={() => setOpenConfirmDialog(true)}
            >
              {getSelectedArchiveState() === 'active' ? 'Archive' : 'Unarchive'}
            </Button>
          )}
        </Box>
      </ButtonActionBar>
      <ArchiveConfirmationDialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        onConfirm={handleConfirmArchive}
        selectedComponents={selectedComponents}
        action={getSelectedArchiveState() === 'active' ? 'archive' : 'unarchive'}
        organizationLabelMap={organizationLabelMap}
      />
    </>
  )
}

export default ManageComponents
